import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Flex, Input, Text } from 'theme-ui';
import { FiTrash2 } from 'react-icons/fi';
import theme from '../../../theme';
import { DeliveryRecord, FolderOption, IDropdownOption } from '../../../../@types';
import { getStageBadgeStyle, getStatusBadgeStyle } from '../../../utils/constent';
import { getPriorityStyles } from '../../../utils/GenerateQueryString';
import FormSelect from '../../forms-component/globalSelect';
import { getTradeBadgeInfo } from './deliveryModalUtils';

export type ExtendedDeliveryColumn = ColumnDef<DeliveryRecord> & {
  accessorKey?: string;
  filterType?: string;
  options?: IDropdownOption[] | FolderOption[];
};

export interface UseDeliveryColumnsParams {
  activeTab: 'ready' | 'completed' | 'manual';
  currentPage: number;
  limit: number;
  isDark: boolean;
  isEditAllowed: boolean;
  userOptions: IDropdownOption[];
  stageOptions: IDropdownOption[];
  projectOptions: IDropdownOption[];
  deliveryTypeOptions: IDropdownOption[];
  statusOptions: IDropdownOption[];
  priorityOptions: IDropdownOption[];
  publishingReadyOptions: IDropdownOption[];
  allTasks?: Array<Record<string, unknown>>;
  onOpenEditModal: (record: DeliveryRecord) => void;
  onCadOperatorChange: (id: string, value: string, option?: IDropdownOption | null) => void;
  onDeliveryDateChange: (id: string, newDate: string) => void;
  onPriorityChange: (id: string, value: string) => void;
  onTogglePublishingReady: (id: string) => void;
  onTogglePmDelivered: (id: string) => void;
  onDeleteManualItem: (id: string) => void;
}

const generateFolderOptions = (): FolderOption[] => {
  const startMonth = 1609;
  const currentYear = new Date().getFullYear() % 100;
  const currentMonth = new Date().getMonth() + 1;
  const folderOptions: FolderOption[] = [];
  for (let year = 16; year <= currentYear; year++) {
    const start = (year === 16) ? startMonth % 100 : 1;
    const end = (year === currentYear) ? currentMonth + 2 : 12;
    for (let month = start; month <= end; month++) {
      const formattedOption = `projects_${year.toString().padStart(2, '0')}${month.toString().padStart(2, '0')}`;
      const optionObject: FolderOption = { label: formattedOption, value: formattedOption };
      folderOptions.push(optionObject);
      if (year === currentYear && month === currentMonth) {
        optionObject.defaultSelected = true;
      }
    }
  }
  folderOptions.sort((a, b) => a.label.localeCompare(b.label));
  return folderOptions;
};

const closeMenuOnTableScroll = (event: Event): boolean => {
  const target = event.target as HTMLElement | null;
  return !target?.closest?.('.an-simple-select__menu');
};

export const useDeliveryColumns = ({
  activeTab,
  currentPage,
  limit,
  isDark,
  isEditAllowed,
  userOptions,
  stageOptions,
  projectOptions,
  deliveryTypeOptions,
  statusOptions,
  priorityOptions,
  publishingReadyOptions,
  allTasks,
  onOpenEditModal,
  onCadOperatorChange,
  onDeliveryDateChange,
  onPriorityChange,
  onTogglePublishingReady,
  onTogglePmDelivered,
  onDeleteManualItem,
}: UseDeliveryColumnsParams): ExtendedDeliveryColumn[] => {
  const navigate = useNavigate();
  const { search } = useLocation();
  return useMemo<ExtendedDeliveryColumn[]>(() => {
    const allColumns: ExtendedDeliveryColumn[] = [
      {
        header: 'No.',
        accessorKey: 'no',
        enableColumnFilter: false,
        cell: ({ row }) => (
          <Box
            onClick={() => onOpenEditModal(row.original)}
            title="Click to edit delivery details"
            sx={{
              margin: '-6px -10px',
              padding: '6px 10px',
              width: 'calc(100% + 20px)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
            }}
          >
            <Text
              variant="Poppins14Normal24lh"
              sx={{
                textAlign: 'center',
                color: (theme?.colors?.primary as string) || '#7743DB',
                fontWeight: 600,
                textDecoration: 'underline',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              {row.original.no ?? ((currentPage - 1) * limit + row.index + 1)}
            </Text>
          </Box>
        ),
      },
      {
        header: 'Stage Name',
        accessorKey: activeTab === 'manual' ? 'stageId' : 'name',
        filterType: 'dropdown',
        options: stageOptions,
        cell: ({ row }) => {
          const defaultStyle = getStageBadgeStyle(row.original.stageName);
          const rawStageId = row.original.stageId;
          const dynamicColor =
            (typeof rawStageId === 'object' && rawStageId !== null
              ? (rawStageId as { color?: string }).color
              : undefined) || (row.original as { stageColor?: string }).stageColor;

          const badgeColor = dynamicColor || defaultStyle.color;
          const stageDocId = activeTab !== 'manual' ? row.original._id : undefined;

          return (
            <Box sx={{ textAlign: 'center' }}>
              <span
                title={stageDocId ? 'Redirect to stage' : undefined}
                onClick={stageDocId ? () => navigate(`/edit-stages/${stageDocId}`, { state: { from: search } }) : undefined}
                style={{
                  cursor: stageDocId ? 'pointer' : undefined,
                  backgroundColor: 'transparent',
                  color: badgeColor,
                  border: `1px solid ${badgeColor}`,
                  padding: '2px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  display: 'inline-block',
                }}
              >
                {row.original.temporaryName || row.original.stageName}
              </span>
            </Box>
          );
        },
      },
      {
        header: 'Project Name',
        accessorKey: 'projectId',
        filterType: 'dropdown',
        options: projectOptions,
        cell: ({ row }) => {
          const pObj = typeof row.original.projectId === 'object' && row.original.projectId ? row.original.projectId : null;
          const pId = pObj?._id || String(row.original.projectId || '');
          const matchedProj = projectOptions.find((p) => String(p.value) === pId);
          const pName = pObj?.name || row.original.projectName || matchedProj?.label || (pId && !/^[0-9a-fA-F]{24}$/.test(pId) && pId !== '-' ? pId : '');
          return (
            <Text sx={{ fontWeight: 500, color: isDark ? '#ffffff' : '#0A0A0A' }}>
              {pName}
            </Text>
          );
        },
      },
      {
        header: 'Folder Number',
        accessorKey: 'folderNumber',
        filterType: 'dropdown',
        options: generateFolderOptions(),
        cell: (info) => {
          const val = info.getValue() as string;
          if (val) return <Text>{val}</Text>;
          const pObj = typeof info.row.original.projectId === 'object' && info.row.original.projectId ? info.row.original.projectId : null;
          return <Text>{pObj?.folderNumber && pObj.folderNumber !== '-' ? pObj.folderNumber : ''}</Text>;
        },
      },
      {
        header: 'PM Manager',
        accessorKey: 'manager',
        filterType: 'dropdown',
        options: userOptions,
        cell: ({ row }) => {
          const projPm =
            typeof row.original.projectId === 'object' && row.original.projectId && row.original.projectId.projectManager
              ? row.original.projectId.projectManager
              : null;
          const pm =
            (projPm && (typeof projPm === 'string' || (projPm as any).FirstName || (projPm as any).LastName || (projPm as any)._id)
              ? projPm
              : null) || row.original.manager;
          const pmId = typeof pm === 'object' && pm ? (pm as { _id?: string })._id : pm;
          const matchedUser = userOptions.find((x) => String(x.value) === String(pmId));
          const name =
            matchedUser?.label ||
            (typeof pm === 'object' && pm
              ? `${(pm as { FirstName?: string; LastName?: string }).FirstName || ''} ${(pm as { FirstName?: string; LastName?: string }).LastName || ''}`.trim() || (pm as any).name || ''
              : typeof pm === 'string'
                ? pm
                : row.original.pmManager || '');
          const validName = name && name !== '-' && !/^[0-9a-fA-F]{24}$/.test(name) ? name : '';
          return <Text>{validName}</Text>;
        },
      },
      {
        header: 'CAD Operator',
        accessorKey: 'cadOperator',
        filterType: 'dropdown',
        options: userOptions,
        cell: ({ row }) => {
          const rawVal = row.original.cadOperator || '';
          const opId = typeof rawVal === 'object' && rawVal ? (rawVal as { _id?: string })._id || '' : String(rawVal || '');
          const matchedOp = userOptions.find((op) => op.value === opId || (typeof rawVal === 'string' && op.label === rawVal));
          const currentVal = matchedOp?.value || opId;
          const objectName =
            typeof rawVal === 'object' && rawVal
              ? `${(rawVal as { FirstName?: string; LastName?: string }).FirstName || ''} ${(rawVal as { FirstName?: string; LastName?: string }).LastName || ''}`.trim()
              : '';
          const rawStringName = typeof rawVal === 'string' && !/^[0-9a-fA-F]{24}$/.test(rawVal) ? rawVal : '';
          const matchedLabel = matchedOp?.label && !/^[0-9a-fA-F]{24}$/.test(matchedOp.label) ? matchedOp.label : '';
          const displayName = matchedLabel || objectName || rawStringName;

          if (activeTab === 'completed' || !isEditAllowed) {
            const noPermissionTooltip = !isEditAllowed ? 'Only Project Managers and Super Admin have permission to edit' : undefined;
            return (
              <Box title={noPermissionTooltip} sx={{ cursor: !isEditAllowed ? 'not-allowed' : 'default', textAlign: 'center' }}>
                <Text variant="Poppins14Normal24lh" sx={{ color: isDark ? '#ffffff' : 'black' }}>
                  {displayName && displayName !== '-' ? displayName : ''}
                </Text>
              </Box>
            );
          }

          const selectOptions =
            currentVal && displayName && !userOptions.some((op) => op.value === currentVal)
              ? [...userOptions, { label: displayName, value: currentVal }]
              : userOptions;

          return (
            <Box sx={{ minWidth: '130px' }}>
              <FormSelect
                placeholder="Select CAD Operator"
                value={currentVal}
                onChange={(selected: IDropdownOption | null) =>
                  onCadOperatorChange(row.original.id, selected?.value || selected?.label || '', selected)
                }
                options={selectOptions}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                closeMenuOnScroll={closeMenuOnTableScroll}
              />
            </Box>
          );
        },
      },
      {
        header: 'Delivery Date',
        accessorKey: 'deliveryDate',
        filterType: 'date',
        cell: ({ row }) => {
          const deliveryDateValue = (row.original.deliveryDate || '') as string;
          const formattedValue = deliveryDateValue ? deliveryDateValue.split('T')[0] : '';
          const isDisabled = activeTab === 'completed' || !isEditAllowed;
          const noPermissionTooltip = !isEditAllowed
            ? 'Only Project Managers and Super Admin have permission to edit'
            : undefined;

          return (
            <div style={{ position: 'relative' }} title={noPermissionTooltip}>
              <Input
                type="date"
                className="custom-datepicker-input"
                name="deliveryDate"
                disabled={isDisabled}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (isDisabled) return;
                  const newDate = event.target.value;
                  onDeliveryDateChange(row.original.id, newDate);
                }}
                value={formattedValue}
                sx={{
                  borderColor: isDark ? 'white' : '#f7efe5',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderRadius: '12px',
                  color: isDark ? 'white' : 'black',
                  cursor: isDisabled ? (!isEditAllowed ? 'not-allowed' : 'default') : 'pointer',
                  appearance: 'textfield',
                  '::placeholder': {
                    color: 'transparent',
                  },
                }}
              />
            </div>
          );
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        filterType: 'dropdown',
        options: statusOptions,
        cell: ({ row }) => {
          const style = getStatusBadgeStyle(row.original.status, row.original.waitingForPayment);
          return (
            <Box sx={{ textAlign: 'center' }}>
              <span
                style={{
                  backgroundColor: style.bg,
                  color: style.color,
                  border: `1px solid ${style.border}`,
                  padding: '3px 8px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.original.status}
              </span>
            </Box>
          );
        },
      },
      {
        header: 'Priority',
        accessorKey: 'priority',
        filterType: 'dropdown',
        options: priorityOptions,
        cell: ({ row }) => {
          const priorityVal = row.original.priority || 'Medium';
          const valLower = priorityVal.toLowerCase();
          let color = '#ea580c';
          if (valLower === 'high') color = '#dc2626';
          if (valLower === 'low') color = '#16a34a';

          if (activeTab === 'completed' || !isEditAllowed) {
            const noPermissionTooltip = !isEditAllowed ? 'Only Project Managers and Super Admin have permission to edit' : undefined;
            return (
              <Box title={noPermissionTooltip} sx={{ cursor: !isEditAllowed ? 'not-allowed' : 'default', textAlign: 'center' }}>
                <Text variant="Poppins14Normal24lh" sx={{ color, fontWeight: 600 }}>
                  {priorityVal}
                </Text>
              </Box>
            );
          }

          return (
            <Box sx={{ minWidth: '110px' }}>
              <FormSelect
                placeholder="Select Priority"
                className={`priority-select priority-${valLower}`}
                value={row.original.priority || ''}
                onChange={(selected: IDropdownOption | null) =>
                  onPriorityChange(row.original.id, selected?.value || selected?.label || '')
                }
                options={priorityOptions}
                styles={getPriorityStyles(row.original.priority || '')}
                formatOptionLabel={(option: IDropdownOption) => {
                  const val = (option.value || option.label || '').toLowerCase();
                  let optColor = '#ea580c';
                  if (val === 'high') optColor = '#dc2626';
                  if (val === 'low') optColor = '#16a34a';
                  return <span style={{ color: optColor, fontWeight: 600 }}>{option.label}</span>;
                }}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                closeMenuOnScroll={closeMenuOnTableScroll}
              />
            </Box>
          );
        },
      },
      {
        header: 'Trade Name List',
        accessorKey: 'tasksNameList',
        enableColumnFilter: false,
        cell: ({ row }) => {
          const rawList = (row.original.tasksNameList ||
            (row.original as unknown as { trades?: unknown[] }).trades ||
            []) as unknown[];
          if (Array.isArray(rawList) && rawList.length > 0) {
            return (
              <Flex sx={{ gap: '6px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                {rawList.map((item: unknown, index: number) => {
                  const badge = getTradeBadgeInfo(item, allTasks, activeTab === 'manual');
                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2px 8px',
                        backgroundColor: badge.color,
                        color: '#FFFFFF',
                        border: `1px solid ${badge.color}`,
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                      title={badge.name}
                    >
                      {badge.shortForm}
                    </Box>
                  );
                })}
              </Flex>
            );
          }
        },
      },
      {
        header: 'Publishing Ready',
        accessorKey: 'publishingReady',
        filterType: 'dropdown',
        options: publishingReadyOptions,
        cell: ({ row }) => {
          const isCompletedTab = activeTab === 'completed';
          const rawCad = row.original.cadOperator;
          const hasCadOperator = Boolean(
            rawCad &&
            (typeof rawCad === 'string'
              ? rawCad.trim().length > 0 && rawCad.trim().toLowerCase() !== 'select cad operator'
              : Boolean(
                (rawCad as { _id?: string; FirstName?: string })?._id ||
                (rawCad as { _id?: string; FirstName?: string })?.FirstName
              ))
          );
          const isPublishingDisabled = isCompletedTab || !hasCadOperator || !isEditAllowed;
          const isChecked = Boolean(row.original.publishingReady);
          const tooltipText = !isEditAllowed
            ? 'Only Project Managers and Super Admin have permission to edit'
            : !isCompletedTab && !hasCadOperator
              ? 'Please select cad operator'
              : undefined;

          return (
            <Box
              sx={{
                textAlign: 'center',
                display: 'inline-block',
                cursor: isPublishingDisabled ? 'not-allowed' : 'pointer',
              }}
              title={tooltipText}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onClick={(e) => {
                  if (isPublishingDisabled) {
                    e.preventDefault();
                  }
                }}
                onChange={() => !isPublishingDisabled && onTogglePublishingReady(row.original.id)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: (theme?.colors?.primary as string) || '#7743DB',
                  cursor: isPublishingDisabled ? 'not-allowed' : 'pointer',
                  opacity: 1,
                  filter: 'none',
                  pointerEvents: 'auto',
                }}
              />
            </Box>
          );
        },
      },
      {
        header: 'PM Delivered',
        accessorKey: 'projectManagerDeliver',
        enableColumnFilter: false,
        cell: ({ row }) => {
          const isCompletedTab = activeTab === 'completed';
          const isManualTab = activeTab === 'manual';
          const statusLower = String(row.original.status || '').toLowerCase().trim();
          const isDelivered = statusLower === 'delivered' || statusLower === 'ipr delivered';
          const isPmDeliveredDisabled = isCompletedTab || isManualTab || !isDelivered || !isEditAllowed;
          const isChecked = Boolean(row.original.pmDelivered || isCompletedTab);
          const tooltipText = !isEditAllowed ? 'Only Project Managers and Super Admin have permission to edit' : undefined;

          return (
            <Box sx={{ textAlign: 'center' }} title={tooltipText}>
              <input
                type="checkbox"
                checked={isChecked}
                onClick={(e) => {
                  if (isPmDeliveredDisabled) {
                    e.preventDefault();
                  }
                }}
                onChange={() => !isPmDeliveredDisabled && onTogglePmDelivered(row.original.id)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: (theme?.colors?.primary as string) || '#7743DB',
                  cursor: isPmDeliveredDisabled ? 'not-allowed' : 'pointer',
                  opacity: 1,
                  filter: 'none',
                }}
              />
            </Box>
          );
        },
      },
    ];

    if (activeTab === 'manual') {
      const manualCols = allColumns.filter(
        (col) =>
          col.accessorKey !== 'status' &&
          col.accessorKey !== 'publishingReady' &&
          col.accessorKey !== 'projectManagerDeliver'
      );
      if (isEditAllowed) {
        manualCols.push({
          header: 'Delete',
          accessorKey: 'actions',
          enableColumnFilter: false,
          cell: ({ row }) => (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                onClick={() => onDeleteManualItem(row.original.id)}
                sx={{
                  cursor: 'pointer',
                  color: '#EF4444',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': { opacity: 0.8 },
                }}
                title="Delete item"
              >
                <FiTrash2 size={16} />
              </Box>
            </Box>
          ),
        });
      }
      return manualCols;
    }

    return allColumns;
  }, [
    navigate,
    search,
    activeTab,
    currentPage,
    limit,
    isDark,
    isEditAllowed,
    userOptions,
    stageOptions,
    projectOptions,
    deliveryTypeOptions,
    statusOptions,
    priorityOptions,
    publishingReadyOptions,
    allTasks,
    onOpenEditModal,
    onCadOperatorChange,
    onDeliveryDateChange,
    onPriorityChange,
    onTogglePublishingReady,
    onTogglePmDelivered,
    onDeleteManualItem,
  ]);
};

export default useDeliveryColumns;
